import {
  Component,
  ElementRef,
  AfterViewInit,
  Input,
  ViewChild,
  ViewEncapsulation,
  OnChanges,
  ChangeDetectionStrategy,
  Inject,
  LOCALE_ID
} from '@angular/core'

import { FormatPipe } from '../../../pipes/format.pipe';
import { ColorService } from '../../../services/color/color.service';
import { SessionService } from '../../../services/session/session.service';;

import { ChartOptions } from '../chart-options'

import * as Highcharts from 'highcharts/highcharts';

const HIGHCHARTS_OPTIONS = {
  chart: {
    style: {
    	color: '#333333',
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontSize: '12px',
    },
    backgroundColor: 'transparent',
  },
  credits: {
    enabled: false
  },
  legend: {
    enabled: true,
    itemStyle: {
    	fontWeight: '500',
      fontSize: '12px',
      color: '#666'
    },
    symbolWidth: 10,
    symbolHeight: 10,
    symbolRadius: 5
  },
  title: null,
  xAxis: [{
    title: null,
    type: 'datetime',
    labels: {
      formatter: function() {
        return Date.create(this.value).setUTC(true).format('{Mon} {d}')
      },
      style: {
        color: '#949497'
      }
    },
    lineColor: 'rgba(158, 158, 158, 0.5)',
		tickLength: 6,
  }],
  yAxis: [{
    title: null,
    labels: {
      style: {
        color: '#949497'
      }
    },
    tickColor: 'rgba(158, 158, 158, 0.5)'
  }],
  plotOptions: {
    series: {
      lineWidth: 3,
      marker: {
        enabled: false,
        symbol: 'circle',
        radius: 2,
        lineColor: null,
      },
      states: {
        hover: {
          lineWidthPlus: 0,
          halo: {
            size: 0
          }
        }
      }
    }
  },
  tooltip: {
    borderWidth: 0,
    shadow: false,
    shared: true,
    useHTML: true
  },
}

@Component({
  selector: 'chart',
  template: '<div #containerRef class="chart-container"></div>',
  styles: [`
    .chart-container {
      width: 100%;
      height: 100%;
    }
  `],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Chart implements AfterViewInit, OnChanges {
  @Input() series: any[]
  @Input() highchartsOptions: any
  @Input() chartOptions: ChartOptions = {}

  @ViewChild('containerRef') containerRef: ElementRef
  instance: any
  window = <any>window
  formatter: any

  constructor(@Inject(LOCALE_ID) locale: string, session: SessionService, private colorService: ColorService) {
    this.formatter = new FormatPipe(locale, session)
  }

  ngAfterViewInit() {
    this.drawChart()
    $(this.window).resize(() => {
      setTimeout(() => {
        this.instance.reflow()
      })
    })
  }

  ngOnChanges() {
    if (this.instance) {
      this.drawChart()
    }
  }

  private drawChart() {
    let options = Object.merge({ colors: this.colorService.defaultColors }, HIGHCHARTS_OPTIONS, {deep: true})
    options = Object.merge(options, this.highchartsOptions, {deep: true})
    this.setHighchartsOptionsFromChartOptions(options)
    this.setTooltipFormatter(options)
    this.setYAxisFormatter(options)
    options = Object.merge(options, { series: this.ensureNullValues(this.series)}, {deep: true})
    this.instance = Highcharts.chart(this.containerRef.nativeElement, options)
  }

  private setHighchartsOptionsFromChartOptions(options) {
    const co = this.chartOptions
    if (co) {
      if (co.colors) {
        options.colors = co.colors
      }

      if (co.backgroundColor) {
        options.chart.backgroundColor = co.backgroundColor
      }

      if (co.hideXAxis) {
        for (const axis of options.xAxis) {
          axis['visible'] = false
        }
      }

      if (co.hideYAxis) {
        for (const axis of options.yAxis) {
          axis['visible'] = false
        }
      }

      if (co.textColor) {
        for (const axis of options.xAxis) {
          axis.labels.style.color = co.textColor
        }
        for (const axis of options.yAxis) {
          axis.labels.style.color = co.textColor
        }
        options.legend.itemStyle.color = co.textColor
      }

      if (co.showLegend) {
        options.legend.enabled = true
      } else if (co.showLegend == false) {
        options.legend.enabled = false
      }

      if (co.title) {
        options.title = {
          align: co.title.align,
          text: co.title.text,
          style: {
            color: co.textColor,
            fontSize: co.title.size ? co.title.size + 'px' : '18px',
            fontWeight: 'bold'
          }
        }
      }
    }
  }

  private setYAxisFormatter(options) {
    const __this = this
    for (const i in options['yAxis']) {
      options['yAxis'][i]['labels']['formatter'] = function() {
        const userOptions = this.chart['series'] && this.chart['series'][0] && this.chart['series'][0]['userOptions']
        const format = userOptions['format']
        if (format !== 'number') {
          return __this.formatter.transform(this.value, userOptions['format'])
        } else {
          return this.axis.defaultLabelFormatter.call(this)
        }
      }
    }
  }

  private setTooltipFormatter(options) {
    let __this = this
    options['tooltip']['formatter'] = function() {
      let html = `
        <table>
      `

      if (this.points) {
        html += `
          <tr>
            <td valign="bottom"><span style="color:#666666; font-size:14px; font-weight:500;" colspan="2">${Date.create(this.x).setUTC(true).format('{Dow}, {Mon} {d}')}</span></td>
          </tr>
        `

        for (const point of this.points) {
          const series = point['series']
          html += `
            <tr>
              <td style="font-size:12px; font-weight:500;"><span style="color:${series['color']}">●</span> ${series['name']}:&nbsp;&nbsp;</td>
              <td style="font-size:12px; font-weight:500;" align="right">${__this.formatter.transform(point.y, series.userOptions.format)}</td>
            </tr>
          `
        }
      } else {
        // e.g. pie chart
        html += `
          <tr>
            <td valign="bottom" colspan="2"><span style="color:#666666; font-size:14px; font-weight:500;" colspan="2">${this.point.name}</span></td>
          </tr>
          <tr>
            <td style="font-size:12px; font-weight:500;"><span style="color:${this.color}">●</span> ${this.point.options.key}:&nbsp;&nbsp;</td>
            <td style="font-size:12px; font-weight:500;" align="right">${__this.formatter.transform((this.percentage / 100), 'percent')}</td>
          </tr>
        `
      }


      html += `<table>`
      return html
    }
  }

  // Highcharts will not render date series properly if values are undefined
  private ensureNullValues(series) {
    const newSeries = series.map(s => {
      const newData = s.data.map(d => {
        if (Object.isObject(d)) {
          return Object.merge(d, {y: d.y || 0})
        } else if (Object.isArray(d)) {
          return d.map(_d => _d || 0)
        } else {
          return d || 0
        }
      })
      return Object.merge(s, {data: newData})
    })
    return newSeries
  }
}
